import { APP_VERSION, blobToDataUrl, dataUrlToBlob, imageDimensions, sha256, uid } from './utils'
import type {
  Archive, ArchiveBundle, ArchiveCollectionBundle, BodyRegion, Calibration, ExportedImage, ImageAsset, Measurement, Neo,
  GridGuide, Observation, PersistedState, Position, Side,
} from './types'

const DB_NAME = 'appnei-local'
const DB_VERSION = 2
const STORE_NAMES = ['settings', 'archives', 'nevi', 'observations', 'images', 'calibrations', 'measurements'] as const
type StoreName = typeof STORE_NAMES[number]

interface Setting {
  key: string
  value: string
}

export interface ObservationInput {
  shotAt: string | null
  note?: string
  gridGuide?: GridGuide
  detailBlob?: Blob
  bodyBlob?: Blob
}

export interface PreparedImage {
  blob: Blob
  mimeType: string
  byteSize: number
  sha256: string
  width: number | null
  height: number | null
}

function now(): string {
  return new Date().toISOString()
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('Operazione IndexedDB non riuscita'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('Transazione IndexedDB non riuscita'))
    transaction.onabort = () => reject(transaction.error ?? new Error('Transazione IndexedDB annullata'))
  })
}

function getAllByArchive<T>(store: IDBObjectStore, archiveId: string): Promise<T[]> {
  const index = store.index('archiveId')
  return request(index.getAll(IDBKeyRange.only(archiveId)))
}

function normalizeArchive(raw: Archive): Archive {
  const candidate = raw as Partial<Archive>
  return {
    ...raw,
    personId: candidate.personId || raw.id,
    personName: candidate.personName?.trim() || 'Persona principale',
  }
}

function makeArchive(personName = 'Persona principale', id = uid('archive')): Archive {
  const timestamp = now()
  return { id, personId: uid('person'), personName: personName.trim() || 'Persona principale', formatVersion: 1, createdAt: timestamp, updatedAt: timestamp, nextNeoNumber: 1, appVersion: APP_VERSION }
}

export function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION)
    open.onupgradeneeded = () => {
      const database = open.result
      for (const name of STORE_NAMES) {
        if (!database.objectStoreNames.contains(name)) {
          const store = database.createObjectStore(name, { keyPath: name === 'settings' ? 'key' : 'id' })
          if (name !== 'settings' && name !== 'archives') store.createIndex('archiveId', 'archiveId', { unique: false })
          if (name === 'observations') store.createIndex('neoId', 'neoId', { unique: false })
          if (name === 'images') store.createIndex('observationId', 'observationId', { unique: false })
          if (name === 'calibrations') store.createIndex('observationId', 'observationId', { unique: false })
          if (name === 'measurements') store.createIndex('observationId', 'observationId', { unique: false })
        }
      }
    }
    open.onsuccess = () => resolve(open.result)
    open.onerror = () => reject(open.error ?? new Error('Impossibile aprire archivio locale'))
  })
}

async function getActiveArchiveId(database: IDBDatabase): Promise<string | null> {
  const transaction = database.transaction('settings', 'readonly')
  const setting = await request(transaction.objectStore('settings').get('activeArchiveId')) as Setting | undefined
  return setting?.value ?? null
}

async function ensureActiveArchive(database: IDBDatabase): Promise<Archive> {
  const activeId = await getActiveArchiveId(database)
  if (activeId) {
    const transaction = database.transaction('archives', 'readonly')
    const archive = await request(transaction.objectStore('archives').get(activeId)) as Archive | undefined
    if (archive) {
      const normalized = normalizeArchive(archive)
      if (normalized.personId !== archive.personId || normalized.personName !== archive.personName) {
        const migration = database.transaction('archives', 'readwrite')
        migration.objectStore('archives').put(normalized)
        await transactionDone(migration)
      }
      return normalized
    }
  }
  const archive = makeArchive()
  const transaction = database.transaction(['archives', 'settings'], 'readwrite')
  transaction.objectStore('archives').put(archive)
  transaction.objectStore('settings').put({ key: 'activeArchiveId', value: archive.id } satisfies Setting)
  await transactionDone(transaction)
  return archive
}

async function readAllArchives(database: IDBDatabase): Promise<Archive[]> {
  const transaction = database.transaction('archives', 'readonly')
  const archives = await request(transaction.objectStore('archives').getAll()) as Archive[]
  return archives.map(normalizeArchive).sort((a, b) => a.personName.localeCompare(b.personName, 'it'))
}

type ArchiveRecords = Pick<PersistedState, 'nevi' | 'observations' | 'images' | 'calibrations' | 'measurements'>

async function readArchiveRecords(database: IDBDatabase, archiveId: string): Promise<ArchiveRecords> {
  const transaction = database.transaction(['nevi', 'observations', 'images', 'calibrations', 'measurements'], 'readonly')
  const [nevi, observations, images, calibrations, measurements] = await Promise.all([
    getAllByArchive<Neo>(transaction.objectStore('nevi'), archiveId),
    getAllByArchive<Observation>(transaction.objectStore('observations'), archiveId),
    getAllByArchive<ImageAsset>(transaction.objectStore('images'), archiveId),
    getAllByArchive<Calibration>(transaction.objectStore('calibrations'), archiveId),
    getAllByArchive<Measurement>(transaction.objectStore('measurements'), archiveId),
  ])
  return { nevi: nevi.sort((a, b) => a.number - b.number), observations: observations.sort((a, b) => a.importedAt.localeCompare(b.importedAt)), images, calibrations, measurements }
}

export async function createPerson(name: string, database?: IDBDatabase): Promise<Archive> {
  const personName = name.trim()
  if (personName.length < 1) throw new Error('Inserisci un nome per la persona.')
  const db = database ?? await openDatabase()
  const archive = makeArchive(personName)
  const transaction = db.transaction(['archives', 'settings'], 'readwrite')
  transaction.objectStore('archives').put(archive)
  transaction.objectStore('settings').put({ key: 'activeArchiveId', value: archive.id } satisfies Setting)
  await transactionDone(transaction)
  return archive
}

export async function switchPerson(archiveId: string, database?: IDBDatabase): Promise<Archive> {
  const db = database ?? await openDatabase()
  const readTransaction = db.transaction('archives', 'readonly')
  const found = await request(readTransaction.objectStore('archives').get(archiveId)) as Archive | undefined
  if (!found) throw new Error('Persona non trovata nell’archivio locale.')
  const archive = normalizeArchive(found)
  const transaction = db.transaction('settings', 'readwrite')
  transaction.objectStore('settings').put({ key: 'activeArchiveId', value: archive.id } satisfies Setting)
  await transactionDone(transaction)
  return archive
}

export async function readState(database?: IDBDatabase): Promise<PersistedState> {
  const db = database ?? await openDatabase()
  const archive = await ensureActiveArchive(db)
  const archives = await readAllArchives(db)
  return { archive, archives, ...(await readArchiveRecords(db, archive.id)) }
}

export async function prepareImage(blob: Blob): Promise<PreparedImage> {
  const dims = await imageDimensions(blob)
  return { blob, mimeType: blob.type || 'application/octet-stream', byteSize: blob.size, sha256: await sha256(blob), ...dims }
}

async function makeObservationImages(archiveId: string, observationId: string, input: ObservationInput): Promise<ImageAsset[]> {
  const images: ImageAsset[] = []
  const candidates: Array<{ role: ImageAsset['role']; blob?: Blob }> = [
    { role: 'detail', blob: input.detailBlob },
    { role: 'body-context', blob: input.bodyBlob },
  ]
  for (const candidate of candidates) {
    if (!candidate.blob) continue
    const prepared = await prepareImage(candidate.blob)
    images.push({ id: uid('image'), archiveId, observationId, role: candidate.role, createdAt: now(), ...prepared })
  }
  return images
}

function toObservation(archiveId: string, neoId: string, input: ObservationInput): Observation {
  const timestamp = now()
  const parsed = input.shotAt ? new Date(input.shotAt) : null
  return {
    id: uid('observation'), archiveId, neoId, shotAt: input.shotAt || null, importedAt: timestamp,
    shotOffsetMinutes: parsed && !Number.isNaN(parsed.getTime()) ? -parsed.getTimezoneOffset() : null,
    note: input.note?.trim() || undefined, gridGuide: input.gridGuide, createdAt: timestamp,
  }
}

export async function createNeo(
  position: Position,
  input: ObservationInput,
  label?: string,
  database?: IDBDatabase,
): Promise<{ neo: Neo; observation: Observation; images: ImageAsset[] }> {
  const db = database ?? await openDatabase()
  const archive = await ensureActiveArchive(db)
  const neoId = uid('neo')
  const observation = toObservation(archive.id, neoId, input)
  const images = await makeObservationImages(archive.id, observation.id, input)
  const transaction = db.transaction(['archives', 'nevi', 'observations', 'images'], 'readwrite')
  const done = transactionDone(transaction)
  const result = new Promise<{ neo: Neo; observation: Observation; images: ImageAsset[] }>((resolve, reject) => {
    const archiveStore = transaction.objectStore('archives')
    const readArchive = archiveStore.get(archive.id)
    readArchive.onerror = () => reject(readArchive.error ?? new Error('Archivio locale non leggibile'))
    readArchive.onsuccess = () => {
      try {
        const currentArchive = readArchive.result as Archive | undefined
        if (!currentArchive) throw new Error('Archivio attivo non trovato.')
        const timestamp = now()
        const neo: Neo = { id: neoId, archiveId: currentArchive.id, number: currentArchive.nextNeoNumber, label: label?.trim() || undefined, position, createdAt: timestamp, updatedAt: timestamp }
        const updatedArchive = { ...currentArchive, nextNeoNumber: currentArchive.nextNeoNumber + 1, updatedAt: timestamp }
        archiveStore.put(updatedArchive)
        transaction.objectStore('nevi').put(neo)
        transaction.objectStore('observations').put({ ...observation, archiveId: currentArchive.id })
        for (const image of images) transaction.objectStore('images').put({ ...image, archiveId: currentArchive.id })
        resolve({ neo, observation: { ...observation, archiveId: currentArchive.id }, images: images.map((image) => ({ ...image, archiveId: currentArchive.id })) })
      } catch (error) { reject(error) }
    }
  })
  const [created] = await Promise.all([result, done])
  return created
}

export async function addObservation(
  neo: Neo,
  input: ObservationInput,
  database?: IDBDatabase,
): Promise<{ observation: Observation; images: ImageAsset[] }> {
  const db = database ?? await openDatabase()
  const observation = toObservation(neo.archiveId, neo.id, input)
  const images = await makeObservationImages(neo.archiveId, observation.id, input)
  const updatedNeo = { ...neo, updatedAt: now() }
  const transaction = db.transaction(['nevi', 'observations', 'images'], 'readwrite')
  transaction.objectStore('nevi').put(updatedNeo)
  transaction.objectStore('observations').put(observation)
  for (const image of images) transaction.objectStore('images').put(image)
  await transactionDone(transaction)
  return { observation, images }
}

export async function updateNeoPosition(neo: Neo, position: Position, database?: IDBDatabase): Promise<Neo> {
  const db = database ?? await openDatabase()
  const updated = { ...neo, position, updatedAt: now() }
  const transaction = db.transaction('nevi', 'readwrite')
  transaction.objectStore('nevi').put(updated)
  await transactionDone(transaction)
  return updated
}

export async function saveCalibration(calibration: Omit<Calibration, 'id' | 'archiveId' | 'createdAt'>, database?: IDBDatabase): Promise<Calibration> {
  const db = database ?? await openDatabase()
  const archive = await ensureActiveArchive(db)
  const saved: Calibration = { ...calibration, id: uid('calibration'), archiveId: archive.id, createdAt: now() }
  const transaction = db.transaction('calibrations', 'readwrite')
  transaction.objectStore('calibrations').put(saved)
  await transactionDone(transaction)
  return saved
}

export async function saveMeasurement(measurement: Omit<Measurement, 'id' | 'archiveId' | 'createdAt'>, database?: IDBDatabase): Promise<Measurement> {
  const db = database ?? await openDatabase()
  const archive = await ensureActiveArchive(db)
  const saved: Measurement = { ...measurement, id: uid('measurement'), archiveId: archive.id, createdAt: now() }
  const transaction = db.transaction('measurements', 'readwrite')
  transaction.objectStore('measurements').put(saved)
  await transactionDone(transaction)
  return saved
}

function stripImage(image: ImageAsset, dataUrl: string): ExportedImage {
  const { blob: _blob, ...metadata } = image
  return { ...metadata, dataUrl }
}

async function makeArchiveBundle(archive: Archive, records: ArchiveRecords): Promise<ArchiveBundle> {
  const images: ExportedImage[] = []
  for (const image of records.images) images.push(stripImage(image, await blobToDataUrl(image.blob)))
  return { formatVersion: 1, exportedAt: now(), archive, nevi: records.nevi, observations: records.observations, calibrations: records.calibrations, measurements: records.measurements, images }
}

export async function exportBundle(database?: IDBDatabase): Promise<ArchiveBundle> {
  const db = database ?? await openDatabase()
  const state = await readState(db)
  return makeArchiveBundle(state.archive, state)
}

export async function exportArchives(archiveIds: string[], database?: IDBDatabase): Promise<ArchiveCollectionBundle> {
  const db = database ?? await openDatabase()
  await ensureActiveArchive(db)
  const archives = await readAllArchives(db)
  const selectedIds = [...new Set(archiveIds)]
  if (selectedIds.length === 0) throw new Error('Seleziona almeno una persona da esportare.')
  const selectedArchives = selectedIds.map((id) => archives.find((archive) => archive.id === id))
  if (selectedArchives.some((archive) => !archive)) throw new Error('Una persona selezionata non è più disponibile.')
  const bundles = await Promise.all(selectedArchives.map(async (archive) => makeArchiveBundle(archive!, await readArchiveRecords(db, archive!.id))))
  return { formatVersion: 2, exportedAt: now(), archives: bundles }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

async function validateArchiveBundle(bundle: unknown): Promise<{ ok: true; bundle: ArchiveBundle } | { ok: false; reason: string }> {
  if (!isRecord(bundle) || bundle.formatVersion !== 1 || !isRecord(bundle.archive)) return { ok: false, reason: 'Formato backup non supportato.' }
  const typed = bundle as unknown as ArchiveBundle
  if (!typed.archive.id || !Array.isArray(typed.nevi) || !Array.isArray(typed.observations) || !Array.isArray(typed.images)) return { ok: false, reason: 'Backup incompleto.' }
  const neoIds = new Set(typed.nevi.map((neo) => neo.id))
  const observationIds = new Set(typed.observations.map((observation) => observation.id))
  for (const observation of typed.observations) if (!neoIds.has(observation.neoId)) return { ok: false, reason: 'Backup incoerente: osservazione senza neo.' }
  for (const image of typed.images) {
    if (!observationIds.has(image.observationId)) return { ok: false, reason: 'Backup incoerente: immagine senza osservazione.' }
    try {
      const blob = await dataUrlToBlob(image.dataUrl)
      if (await sha256(blob) !== image.sha256) return { ok: false, reason: 'Backup corrotto: hash immagine non valido.' }
    } catch {
      return { ok: false, reason: 'Backup corrotto: immagine illeggibile.' }
    }
  }
  return { ok: true, bundle: { ...typed, archive: normalizeArchive(typed.archive) } }
}

export async function validateBundle(bundle: unknown): Promise<{ ok: true; bundle: ArchiveBundle } | { ok: false; reason: string }> {
  return validateArchiveBundle(bundle)
}

export async function validateBackupPayload(payload: unknown): Promise<{ ok: true; bundles: ArchiveBundle[] } | { ok: false; reason: string }> {
  if (!isRecord(payload)) return { ok: false, reason: 'Formato backup non supportato.' }
  if (payload.formatVersion === 1) {
    const validation = await validateArchiveBundle(payload)
    return validation.ok ? { ok: true, bundles: [validation.bundle] } : validation
  }
  if (payload.formatVersion !== 2 || !Array.isArray(payload.archives) || payload.archives.length === 0) return { ok: false, reason: 'Backup multipersona incompleto.' }
  const validations = await Promise.all(payload.archives.map((bundle) => validateArchiveBundle(bundle)))
  const failed = validations.find((validation) => !validation.ok)
  if (failed && !failed.ok) return failed
  const bundles = validations.flatMap((validation) => validation.ok ? [validation.bundle] : [])
  if (new Set(bundles.map((bundle) => bundle.archive.id)).size !== bundles.length) return { ok: false, reason: 'Backup incoerente: persona duplicata.' }
  return { ok: true, bundles }
}

async function materializeImages(bundle: ArchiveBundle): Promise<ImageAsset[]> {
  const importedImages: ImageAsset[] = []
  for (const image of bundle.images) {
    const blob = await dataUrlToBlob(image.dataUrl)
    importedImages.push({
      id: image.id, archiveId: bundle.archive.id, observationId: image.observationId, role: image.role,
      blob, mimeType: image.mimeType, byteSize: image.byteSize, sha256: image.sha256,
      width: image.width, height: image.height, createdAt: image.createdAt,
    })
  }
  return importedImages
}

async function importValidatedBundles(bundles: ArchiveBundle[], database: IDBDatabase): Promise<void> {
  const importedImages = (await Promise.all(bundles.map(materializeImages))).flat()
  const transaction = database.transaction(['settings', 'archives', 'nevi', 'observations', 'images', 'calibrations', 'measurements'], 'readwrite')
  const stores = {
    archives: transaction.objectStore('archives'), nevi: transaction.objectStore('nevi'), observations: transaction.objectStore('observations'),
    images: transaction.objectStore('images'), calibrations: transaction.objectStore('calibrations'), measurements: transaction.objectStore('measurements'),
  }
  for (const bundle of bundles) {
    for (const [name, store] of Object.entries(stores)) {
      if (name === 'archives') continue
      const index = store.index('archiveId')
      const records = await request(index.getAllKeys(IDBKeyRange.only(bundle.archive.id))) as IDBValidKey[]
      for (const key of records) store.delete(key)
    }
    stores.archives.put(bundle.archive)
    for (const neo of bundle.nevi) stores.nevi.put(neo)
    for (const observation of bundle.observations) stores.observations.put(observation)
    for (const image of importedImages.filter((item) => item.archiveId === bundle.archive.id)) stores.images.put(image)
    for (const calibration of bundle.calibrations ?? []) stores.calibrations.put(calibration)
    for (const measurement of bundle.measurements ?? []) stores.measurements.put(measurement)
  }
  transaction.objectStore('settings').put({ key: 'activeArchiveId', value: bundles[0].archive.id } satisfies Setting)
  await transactionDone(transaction)
}

export async function importBundle(bundle: ArchiveBundle, database?: IDBDatabase): Promise<void> {
  const validation = await validateArchiveBundle(bundle)
  if (!validation.ok) throw new Error(validation.reason)
  const db = database ?? await openDatabase()
  await importValidatedBundles([validation.bundle], db)
}

export async function importBackup(payload: unknown, database?: IDBDatabase): Promise<number> {
  const validation = await validateBackupPayload(payload)
  if (!validation.ok) throw new Error(validation.reason)
  const db = database ?? await openDatabase()
  await importValidatedBundles(validation.bundles, db)
  return validation.bundles.length
}

export async function getStorageStatus(): Promise<{ usage: number | null; quota: number | null; persisted: boolean | null }> {
  if (typeof navigator === 'undefined' || !navigator.storage) return { usage: null, quota: null, persisted: null }
  const estimate = await navigator.storage.estimate()
  const persisted = 'persisted' in navigator.storage ? await navigator.storage.persisted() : null
  return { usage: estimate.usage ?? null, quota: estimate.quota ?? null, persisted }
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false
  return navigator.storage.persist()
}
